import {
  setMiniAppBackgroundColor,
  setMiniAppBottomBarColor,
  setMiniAppHeaderColor,
  themeParams,
} from '@telegram-apps/sdk-react';

import { insideTelegram, safe } from '../telegram/env';

/** Фон страницы и полоса таб-бара — те же, что --paper и --tabbar-bg в styles.css. */
const CHROME = {
  light: { paper: '#f2f2f7', bar: '#f2f2f7' },
  dark: { paper: '#000000', bar: '#000000' },
} as const;

/**
 * Шапка, фон и нижняя полоса клиента — в цвет бумаги приложения. Палитра своя,
 * а не из темы Telegram, поэтому красим готовыми цветами и перекрашиваем при
 * смене светлой/тёмной.
 */
function paintTelegramChrome(dark: boolean): void {
  if (!insideTelegram()) return;
  const { paper, bar } = dark ? CHROME.dark : CHROME.light;
  safe(() => {
    if (setMiniAppHeaderColor.isAvailable()) setMiniAppHeaderColor(paper);
  });
  safe(() => {
    if (setMiniAppBackgroundColor.isAvailable()) setMiniAppBackgroundColor(paper);
  });
  safe(() => {
    if (setMiniAppBottomBarColor.isAvailable()) setMiniAppBottomBarColor(bar);
  });
}

let currentDark = false;

/** Вызывается, когда miniApp смонтирован: до этого красить нечем. */
export function repaintTelegramChrome(): void {
  paintTelegramChrome(currentDark);
}

/** Что отдаёт telegram-web-app.js (подключён в index.html): тема самого клиента. */
interface TelegramWebApp {
  colorScheme?: 'light' | 'dark';
  onEvent?: (event: 'themeChanged', handler: () => void) => void;
}

function telegramWebApp(): TelegramWebApp | undefined {
  return (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
}

/**
 * Светлое/тёмное: класс .dark на <html>.
 *
 * В клиенте источник правды — Telegram.WebApp.colorScheme: это прямой ответ
 * клиента «светлая или тёмная у меня тема», и событие themeChanged при смене
 * (в том числе когда тема Telegram следует за системой). Сигнал isDark из SDK —
 * лишь запасной путь: он угадывает по яркости bg_color и, пока параметры темы не
 * смонтированы, считает тему тёмной. Вне Telegram — системная настройка браузера.
 */
export function initColorScheme(): void {
  const apply = (dark: boolean): void => {
    currentDark = dark;
    document.documentElement.classList.toggle('dark', dark);
    paintTelegramChrome(dark);
  };

  const systemMedia = window.matchMedia('(prefers-color-scheme: dark)');

  if (insideTelegram()) {
    const webApp = telegramWebApp();
    if (webApp?.colorScheme) {
      apply(webApp.colorScheme === 'dark');
      webApp.onEvent?.('themeChanged', () => apply(webApp.colorScheme === 'dark'));
      return;
    }
    try {
      const { isDark, state } = themeParams;
      apply(isDark());
      state.sub(() => apply(isDark()));
      return;
    } catch {
      // Сигналы не инициализировались (странная сборка клиента) — ниже системная тема.
    }
  }

  apply(systemMedia.matches);
  systemMedia.addEventListener('change', (event) => apply(event.matches));
}